import {
  Card,
  Drawer,
  SkeletonLoader,
  Table,
} from '@insight-health-ai/roseate/components';
import { useGetAppointmentDetails } from 'src/hooks/appointment';
import { useGetAppointmentNotifications } from 'src/hooks/appointment/useGetAppointmentNotifications';
import { Appointment, AppointmentNotification } from 'src/models';
import {
  useAppointmentActions,
  UseAppointmentActionsProps,
} from '../list/actions';
import {
  DetailsBar,
  CopyToClipboard,
  DetailsBarProps,
  ErrorScreen,
} from 'src/components';
import { formatStaticDate, getPractitionerName } from 'src/utils';
import { Popover } from 'antd';
import {
  CalendarDate,
  EyeOff,
  Mail01,
  CursorClick02,
  MessageDotsSquare,
  ImageUser,
  AlertTriangle,
  InfoSquare,
  UserLeft01,
  Mail04,
} from '@insight-health-ai/roseate/icons';
import { useTheme } from '@insight-health-ai/roseate/theme';

type NotificationHistoryDrawerProps = {
  appointmentUuid: Appointment['appt_uuid'];
  open: boolean;
  onClose: () => void;
  title?: string;
} & Pick<UseAppointmentActionsProps, 'onEditAppointment'>;

export const NotificationHistoryDrawer = ({
  appointmentUuid,
  open,
  onClose,
  title,
  onEditAppointment,
}: NotificationHistoryDrawerProps) => {
  const { theme } = useTheme();
  const { data: appointment, isLoading: isLoadingAppointment } =
    useGetAppointmentDetails(
      {
        appt_uuid: appointmentUuid,
      },
      {
        enabled: open && !!appointmentUuid,
      }
    );

  const { actions: drawerActionProps } = useAppointmentActions({
    appointmentUuid,
    onEditAppointment,
    onDeleteCallback: onClose,
  });

  const {
    data: notifications,
    isLoading: isLoadingNotifications,
    error: notificationError,
    refetch: refetchNotifications,
  } = useGetAppointmentNotifications(
    {
      appointment_id: appointment?.appointment_id!,
    },
    { enabled: !!appointment?.appointment_id && open }
  );

  const renderDetails = () => {
    if (isLoadingAppointment || !appointment) {
      return null;
    }

    return (
      <DetailsBar
        details={[
          [
            {
              title: 'Practitioner',
              content:
                (appointment.practitioner_firstName ||
                  appointment.patient_lastName) &&
                getPractitionerName({
                  first_name: appointment.practitioner_firstName,
                  // @ts-expect-error - TODO: fix this
                  last_name: appointment.practitioner_last_name,
                  title: appointment.practitioner_title,
                }),
              icon: <UserLeft01 />,
              fallback: 'Unknown',
            },
            {
              title: 'Appointment Date',
              content: appointment.start_date,
              icon: <CalendarDate />,
              fallback: 'Unknown',
            },
          ],
          [
            {
              title: 'Patient',
              content:
                appointment.patient_firstName || appointment.patient_lastName
                  ? [
                      appointment.patient_firstName,
                      appointment.patient_lastName,
                    ]
                      .filter(Boolean)
                      .join(' ')
                  : null,
              icon: <ImageUser />,
              fallback: 'Unknown',
            },
            {
              title: 'Date of birth',
              content:
                appointment.birth_date ||
                appointment.patient_details.birth_date_iso
                  ? formatStaticDate(
                      appointment.birth_date ||
                        appointment.patient_details.birth_date_iso,
                      'MM/DD/YYYY'
                    )
                  : null,
              icon: <CalendarDate />,
              fallback: 'Unknown',
            },
            {
              title: 'ID',
              content: appointment.patient_id ? (
                <CopyToClipboard text={String(appointment.patient_id)} />
              ) : null,
              icon: <InfoSquare />,
              fallback: 'Unknown',
            },
            appointment.patient_ehr_id && {
              title: 'EHR ID',
              content: (
                <CopyToClipboard text={String(appointment.patient_ehr_id)} />
              ),
              icon: <InfoSquare />,
              fallback: 'Unknown',
            },
          ].filter(Boolean) as DetailsBarProps['details'][number],
        ]}
      />
    );
  };
  const renderNotificationHistory = () => {
    if (isLoadingNotifications) {
      return <SkeletonLoader active />;
    }

    if (notificationError) {
      return (
        <ErrorScreen
          errorType="server"
          retryButton={{ onClick: () => refetchNotifications() }}
        />
      );
    }

    const notificationsTableColumns = [
      {
        title: 'Date',
        dataIndex: 'notification_date',
        key: 'date',
        render: (text: string, record: AppointmentNotification) => {
          return <span>{text}</span>;
        },
      },
      {
        title: 'Sent to',
        dataIndex: 'sent_to',
        key: 'sent_to',
        render: (text: string, record: AppointmentNotification) => {
          return (
            <span className="flex items-center gap-2">
              {record.delivery_method === 'email' ? (
                <Mail01 className="text-blue" />
              ) : (
                <MessageDotsSquare className="text-green" />
              )}
              {text}
            </span>
          );
        },
      },
      {
        title: 'Delivery status',
        dataIndex: 'delivery_status',
        key: 'delivery_status',
        render: (text: string, record: AppointmentNotification) => {
          let deliveryStatusIcon;
          let statusTooltipText;
          let iconColor;
          switch (text?.toLowerCase()) {
            case 'clicked':
            case 'click':
              deliveryStatusIcon = <CursorClick02 />;
              statusTooltipText = 'Clicked';
              iconColor = theme.colors.primary.DEFAULT;
              break;
            case 'opened':
            case 'open':
              deliveryStatusIcon = <Mail04 />;
              statusTooltipText = 'Opened';
              iconColor = theme.colors.primary.DEFAULT;
              break;
            case 'delivery':
            case 'delivered':
              statusTooltipText = 'Delivered';
              deliveryStatusIcon = <EyeOff />;
              iconColor = theme.colors.primary.DEFAULT;
              break;
            case 'sent':
            case 'send':
              statusTooltipText = 'Sent';
              deliveryStatusIcon = <EyeOff />;
              iconColor = theme.colors.primary.DEFAULT;
              break;
            case 'failed':
            case 'complaint':
              statusTooltipText = 'Failed';
              deliveryStatusIcon = <AlertTriangle />;
              iconColor = theme.colors.error.DEFAULT;
              break;
            case 'bounced':
            case 'bounce':
              statusTooltipText = 'Bounced';
              deliveryStatusIcon = <AlertTriangle />;
              iconColor = theme.colors.error.DEFAULT;
              break;
            case 'rejected':
            case 'reject':
              statusTooltipText = 'Rejected';
              deliveryStatusIcon = <AlertTriangle />;
              iconColor = theme.colors.error.DEFAULT;
              break;
            case 'exception':
              statusTooltipText = 'Exception';
              deliveryStatusIcon = <AlertTriangle />;
              iconColor = theme.colors.error.DEFAULT;
              break;
            case 'phone number is invalid':
            case 'email is invalid':
              statusTooltipText = text;
              deliveryStatusIcon = <AlertTriangle />;
              iconColor = theme.colors.error.DEFAULT;
              break;
            default:
              deliveryStatusIcon = text;
              iconColor = theme.colors.info.DEFAULT;
          }
          return (
            <div className="flex">
              <Popover
                placement="topLeft"
                content={
                  <span className="capitalize">{statusTooltipText}</span>
                }
              >
                <div style={{ color: iconColor }}>{deliveryStatusIcon}</div>
              </Popover>
            </div>
          );
        },
      },
      {
        title: 'Sent by',
        dataIndex: 'sent_by',
        key: 'sent_by',
        render: (text: string, record: AppointmentNotification) => {
          return <span>{text}</span>;
        },
      },
    ];

    return (
      <>
        <Card
          title="Sent notifications"
          padding={{ body: 'none', header: 'sm' }}
        >
          <Table
            data={notifications?.sent_notifications || []}
            columns={notificationsTableColumns}
            pagination={{
              pageSize: 5,
              size: 'small',
              position: ['bottomRight'],
            }}
          />
        </Card>
        <Card
          title="Scheduled notifications"
          padding={{ body: 'none', header: 'sm' }}
        >
          <Table
            data={notifications?.schedulded_notifications || []}
            columns={notificationsTableColumns}
            pagination={{
              pageSize: 5,
              size: 'small',
              position: ['bottomRight'],
            }}
          />
        </Card>
      </>
    );
  };

  return (
    <Drawer
      padding={{ body: 'sm' }}
      title={title || 'Notification History'}
      className="insight-background"
      open={open}
      onClose={onClose}
      loading={isLoadingAppointment || isLoadingNotifications}
      headerActions={drawerActionProps}
      size="lg"
    >
      {renderDetails()}
      <div className="flex-1 h-full flex flex-col mt-4 gap-4 overflow-y-auto">
        {renderNotificationHistory()}
      </div>
    </Drawer>
  );
};
